import { Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { Subject, Subscription } from 'rxjs';
import { throttleTime } from 'rxjs/operators';
import { ITableDataOption } from '../../../interfaces/table/table-data-option';
import { ITableExtraParams } from '../../../interfaces/table/table-extra-params';
import { ITableOrder } from '../../../interfaces/table/table-order';
import { TableConfigService } from '../../../services/table/table-config.service';
import { sortByKey } from '../../../services/utils/utilities';
import { RowThickness } from '../table';

@Component({
  selector: 'app-data-table',
  templateUrl: './data-table.component.html',
  styleUrls: ['./data-table.component.scss'],
})
export class DataTableComponent implements OnInit, OnDestroy {
  @ViewChild('target', { static: true }) public targetElRef: ElementRef;

  @Output()
  public sendRowInfo = new EventEmitter<any>();
  private debouncer: Subject<any> = new Subject<any>();
  private subscription: Subscription;

  @Input()
  public set dataset(dataset: ITableDataOption[][]) {
    if (dataset) {
      this.allTableData = dataset;
      this.refreshPage();
      this.tableConfigService.subcriptionDataSet();
    }
  }

  @Input()
  public set dataSort(dataSort: ITableOrder) {
    if (dataSort && dataSort.key) {
      this.sortByKey(dataSort);
    }
  }

  @Input()
  public set pagination(pagination: number) {
    if (pagination) {
      this.currentPage = pagination;
      this.refreshPage();
      this.scrollView();
    }
    this.currentPage = 1;
  }

  @Input()
  public set quantity(quantity: number) {
    if (quantity) {
      this.quantityPerPage = quantity;
      this.refreshPage();
    }
  }

  @Input()
  public set tableExtraParams(tableExtraParams: ITableExtraParams) {
    if (tableExtraParams) {
      this.extraParams = tableExtraParams;
      this.refreshPage();
    }
  }

  // AllTableData is original data
  // tableData is the data to show
  public allTableData: ITableDataOption[][];
  public tableData: ITableDataOption[][];
  public extraParams: ITableExtraParams;

  public currentPage: number;
  public quantityPerPage: number;
  public rowThicknessEnum = RowThickness;

  constructor(private tableConfigService: TableConfigService) {
    this.currentPage = 1;
    this.allTableData = [];
    this.tableData = this.allTableData;
  }

  /**
   * On init component.
   */
  public ngOnInit(): void {
    this.subscription = this.debouncer.pipe(throttleTime(2000)).subscribe((value) => this.sendRowInfo.emit(value));
  }

  /**
   * Function to sort data
   *
   * @param key Key to order
   * @param order Alphabetical order asc or desc
   */
  public sortByKey(dataSort: ITableOrder): void {
    const positionArray: any[] = [];
    let positionArraySorted: any[] = [];
    let i = 0;
    for (const row of this.allTableData) {
      positionArray.push({ position: i, value: row.find((v) => v.key === dataSort.key).value });
      i++;
    }

    positionArraySorted = sortByKey(positionArray, 'value', dataSort.order, this.tableConfigService.isDateColumn(dataSort.key));
    if (dataSort.key === 'lastUbication') {
      positionArraySorted = sortByKey(positionArraySorted, 'value', dataSort.order);
    }

    const newTableData: ITableDataOption[][] = [];
    for (const array of positionArraySorted) {
      newTableData.push(this.allTableData[array.position]);
    }

    this.allTableData = newTableData;
    this.refreshPage();
  }

  /**
   * Function to change the quantity per page
   *
   * @param quantity New quantity per page
   */
  public refreshPage(): void {
    const tableData = Object.assign([], this.allTableData) as ITableDataOption[][];

    if (this.extraParams && this.extraParams.minimalRows > this.allTableData.length) {
      const trashData: ITableDataOption[] = [];

      for (const data of this.allTableData[0]) {
        trashData.push({
          key: data.key,
          value: '',
        });
      }

      for (let i = 0; i < this.extraParams.minimalRows - this.allTableData.length; i++) {
        tableData.push(trashData);
      }
    }

    if (this.currentPage && this.quantityPerPage) {
      const dataToShow = [];
      const quantityFrom = (this.currentPage - 1) * this.quantityPerPage;

      for (let i = quantityFrom; i < quantityFrom + this.quantityPerPage; i++) {
        if (!tableData[i]) {
          break;
        }
        dataToShow.push(tableData[i]);
      }

      this.tableData = dataToShow;
    }
  }

  /**
   * Emit information of the selected cell
   *
   * @param isClickable Boolean from datasetConfig to evaluate if cell is clickable
   * @param row All cells in clicked row
   * @param cell Clicked cell
   */
  public sendRowAndCellInfo(isClickable: boolean, row: ITableDataOption[], cell: ITableDataOption): void {
    if (isClickable) {
      const selectedRow = { row, cell };
      this.debouncer.next(selectedRow);
    }
  }

  /**
   * On destroy component.
   */
  public ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
  }

  /**
   * scroll up to star table
   */
  public scrollView(): void {
    const element = this.targetElRef.nativeElement as HTMLDivElement;
    const elementParent = this.targetElRef.nativeElement.parentNode as HTMLDivElement;
    if (elementParent.scrollHeight > elementParent.clientHeight) {
      element.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'end' });
    }
  }
}
